import { formatInTimeZone } from "date-fns-tz";
import { useCallback } from "react";
import { IconButton, Typography, SidePanel, Icon, Card, Divider, Alert, Tooltip } from "@/components/atoms";
import { BookingFlight, BookingFlightStatus } from "@/redux/slices/booking/types";
import { clsx, getAssetUrl } from "@/utils";
import { BookingFlightStatusCode } from "../../common/BookingFlightStatusCode/BookingFlightStatusCode";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";

interface FlightDetailsProps {
  open: boolean;
  onClose: () => void;
}

interface DetailsProps {
  flight: BookingFlight;
  timezone: string;
}

export const FlightDetails = ({ open, onClose }: FlightDetailsProps) => {
  const booking = useSelectedBooking();
  const flight = booking.stops.find((stop) => stop.flight)?.flight;

  return (
    <SidePanel open={open} onClose={onClose} width={550} focused>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="Airplane" className="text-neutral-dark-gray" size="lg" />
          <Typography variant="title">Flight Details ({booking.jobNumber})</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={onClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <section>{flight && <Details flight={flight} timezone={booking.dates.timezone} />}</section>
    </SidePanel>
  );
};

const Details = ({ flight, timezone }: DetailsProps) => {
  const { arrival, status, number, codeShare, operatingAs, delay } = flight;
  const statusClasses = { "is-cancelled": status === "cancelled" };

  const renderAlerts = () => {
    switch (status) {
      case "not_found":
        return <Alert type="warning" message="Flight details cannot be verified" />;

      case "unknown":
        return <Alert type="info" message="Flight details will be available 24 hours before the flight." />;
      default:
        return null;
    }
  };

  const renderFlightStatusCode = () => {
    const activeStatusList = ["scheduled", "en-route"];
    if (typeof delay === "number" && activeStatusList.includes(status) && delay !== 0) {
      return (
        <Tooltip content={`Running ${Math.abs(delay)} minutes ${delay > 0 ? "late" : "early"}`} placement="left">
          <BookingFlightStatusCode status={status} delay={delay} type="default" />
        </Tooltip>
      );
    }

    return <BookingFlightStatusCode status={status} type="default" />;
  };

  return (
    <div className={clsx("group flex min-h-full flex-col space-y-2.5 p-5", statusClasses)}>
      {renderAlerts()}
      <Card className="flex flex-col">
        <div className="flex flex-row items-center">
          <div className="flex flex-row">
            {flight.airline?.avatar ? (
              <img
                src={getAssetUrl(flight.airline?.avatar, { height: 48, width: 250, fit: "cover" })}
                alt={flight.airline.name}
                className="h-[24px] w-[125px]"
              />
            ) : (
              <Typography variant="action">{flight.airline?.name || "-"}</Typography>
            )}
          </div>
          <div className="flex flex-1 justify-end">{renderFlightStatusCode()}</div>
        </div>
        <Typography variant="h2" className="mt-3">{`Flight ${number}`}</Typography>
        <Typography variant="paragraph" className="mt-1">
          {codeShare
            ? [operatingAs, ...codeShare]
                .filter((code) => code !== number)
                .filter(Boolean)
                .join(", ")
            : ""}
        </Typography>
      </Card>
      <Card className="flex flex-col gap-y-1">
        <Typography variant="action">Arrival</Typography>
        <div className="flex flex-row items-center gap-x-1.5">
          <Icon name="Location" className="text-neutral-dark-gray" size="md" />
          <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
            {arrival.airport?.name || "-"}
          </Typography>
        </div>
      </Card>
      <Card className="flex flex-col gap-y-1">
        <Typography variant="action">Scheduled Gate Time</Typography>
        <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
          {arrival.scheduled ? formatInTimeZone(new Date(arrival.scheduled), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
        </Typography>
      </Card>
      <Card className="flex flex-col gap-y-3">
        <LandingTime flight={flight} timezone={timezone} />
        <Divider />
        <GateTime flight={flight} timezone={timezone} />
        <Divider />
        <div className="flex flex-col gap-y-1">
          <Typography variant="action">Terminal</Typography>
          <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
            {arrival.terminal || "-"}
          </Typography>
        </div>
      </Card>
      <FlightProgress status={status} hasArrived={!!arrival.actual} hasGated={!!arrival.gateActual} />
    </div>
  );
};

const FlightProgress = ({ status, hasArrived, hasGated }: { status: BookingFlightStatus; hasArrived: boolean; hasGated: boolean }) => {
  const renderDepartureStatusIcon = useCallback(() => {
    switch (status) {
      case "diverted":
      case "en-route":
      case "early":
      case "landed":
        return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;

      case "cancelled":
        return <Icon name="CloseCircle" size="md" variant="Bold" className="text-danger" />;

      default:
        return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
    }
  }, [status]);

  const renderLandedStatusIcon = useCallback(() => {
    switch (status) {
      case "landed":
        return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;

      case "cancelled":
        return <Icon name="CloseCircle" size="md" variant="Bold" className="text-danger" />;

      default:
        return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
    }
  }, [status]);

  const renderGatedStatus = useCallback(() => {
    if (hasGated) {
      return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;
    }

    return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
  }, [hasGated]);

  return (
    <Card>
      <Typography variant="action">Flight Progress</Typography>
      <div className="mt-3 grid gap-2">
        <Typography className="flex items-center gap-x-2" variant="paragraph">
          {renderDepartureStatusIcon()}
          Departed
        </Typography>
        <Typography className="flex items-center gap-x-2" variant="paragraph">
          {renderLandedStatusIcon()}
          Landed
        </Typography>
        {status === "landed" && hasArrived && (
          <Typography className="flex items-center gap-x-2" variant="paragraph">
            {renderGatedStatus()}
            Arrived / Gated
          </Typography>
        )}
      </div>
    </Card>
  );
};

const LandingTime = ({ flight, timezone }: { flight: BookingFlight; timezone: string }) => {
  const { arrival } = flight;
  const isActual = arrival.actual;
  const arrivalTime = isActual ? arrival.actual : arrival.estimated;

  return (
    <div className="flex flex-col gap-y-1">
      <Typography variant="action">{isActual ? "Actual" : "Est."} Landing Time</Typography>
      <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
        {arrivalTime ? formatInTimeZone(new Date(arrivalTime), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
      </Typography>
    </div>
  );
};

const GateTime = ({ flight, timezone }: { flight: BookingFlight; timezone: string }) => {
  const { arrival } = flight;
  const isActual = arrival.gateActual;
  const gateTime = isActual ? arrival.gateActual : arrival.gateEstimated;

  return (
    <div className="flex flex-col gap-y-1">
      <Typography variant="action">{isActual ? "Actual" : "Est."} Gate Time</Typography>
      <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
        {gateTime ? formatInTimeZone(new Date(gateTime), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
      </Typography>
    </div>
  );
};
